// ============================================================================
// objectives.js — mission win/lose logic. A level lists `objectives` (all must
// complete to win) and `fail` conditions (any triggers defeat). Benjen's death
// is always a loss. Trackers expose {label, cur, max, done} for the HUD.
// The battle outcome is decided HERE, not in sim.js.
// ============================================================================

import { TEAM_PLAYER, ST_DEAD } from '../core/constants.js';
import { EV } from '../core/events.js';

function findWarlord(world, name) {
  for (const w of world.warlords) if (w.warlord && w.warlord.name === name) return w;
  if (world.dynast && world.dynast.warlord && world.dynast.warlord.name === name) return world.dynast;
  return null;
}
function findGeneral(world, name) {
  for (const g of world.generals) if (g.warlord && g.warlord.name === name) return g;
  if (world.warmaster && world.warmaster.warlord && world.warmaster.warlord.name === name) return world.warmaster;
  return null;
}
const dead = (u) => !u || u.state === ST_DEAD || !u._active;

function makeTracker(world, o, isFail) {
  const t = { type: o.type, p: o, done: false, failed: false, cur: 0, max: 1, label: '', fail: !!isFail };
  switch (o.type) {
    case 'rout': t.max = o.count; t.label = `Rout the Magmar host — slay ${o.count}`; break;
    case 'slayWarlords': t.max = o.count; t.label = `Slay ${o.count} Magmar warlord${o.count > 1 ? 's' : ''}`; break;
    case 'slayWarlord': t.label = `Slay ${o.name}`; break;
    case 'slayDynast': t.label = 'Slay Ourang-Vael, the Magmar Dynast'; break;
    case 'capture': t.max = o.count; t.label = `Capture ${o.count} bastion${o.count > 1 ? 's' : ''}`; break;
    case 'captureAll': t.max = Math.max(1, world.bastions.length); t.label = 'Capture every bastion'; break;
    case 'survive': t.max = o.time; t.label = `Hold the line for ${o.time}s`; break;
    case 'reach': t.label = o.label || 'Reach the objective'; break;
    case 'morale': t.max = o.value; t.label = `Turn the tide — morale ${o.value}`; break;
    case 'protect': t.label = `Keep ${o.name} alive`; break;
    case 'timeout': t.max = o.time; t.label = `Win before ${o.time}s`; break;
    case 'holdBastions': t.label = `Hold your bastion${(o.ids && o.ids.length > 1) ? 's' : ''}`; break;
    default: t.label = o.type;
  }
  return t;
}

export function buildObjectives(world, level) {
  world.objectives = (level.objectives || []).map((o) => makeTracker(world, o, false));
  world._failConds = (level.fail || []).map((o) => makeTracker(world, o, true));
  world.outcome = null; world.failReason = '';
}

function evalTracker(world, t) {
  const o = t.p, p = world.player;
  switch (t.type) {
    case 'rout': t.cur = world.killCount; t.done = t.cur >= t.max; break;
    case 'slayWarlords': t.cur = world.warlordsSlain; t.done = t.cur >= t.max; break;
    case 'slayWarlord': { const w = findWarlord(world, o.name); t.done = dead(w); t.cur = t.done ? 1 : 0; break; }
    case 'slayDynast': t.done = dead(world.dynast); t.cur = t.done ? 1 : 0; break;
    case 'capture': t.cur = world.bastions.filter((b) => b.team === TEAM_PLAYER).length; t.done = t.cur >= t.max; break;
    case 'captureAll': t.cur = world.bastions.filter((b) => b.team === TEAM_PLAYER).length; t.done = t.cur >= t.max; break;
    case 'survive': t.cur = Math.min(world.levelTime, t.max); t.done = world.levelTime >= t.max; break;
    case 'reach': { const dx = p.x - o.x, dz = p.z - o.z; t.done = Math.hypot(dx, dz) <= (o.r || 12); t.cur = t.done ? 1 : 0; break; }
    case 'morale': t.cur = Math.min(world.morale, t.max); t.done = world.morale >= t.max; break;
    // ---- fail conditions ----
    case 'protect': { const g = findGeneral(world, o.name); if (g && dead(g)) t.failed = true; break; }
    case 'timeout': t.cur = Math.min(world.levelTime, t.max); if (world.levelTime >= t.max) t.failed = true; break;
    case 'holdBastions': {
      for (const id of (o.ids || [])) { const b = world.bastions[id]; if (b && b.team !== TEAM_PLAYER) { t.failed = true; break; } }
      break;
    }
  }
}

function win(world) {
  if (world.outcome) return;
  world.outcome = 'win'; world.won = true; world.gameOver = true;
  world.events.emit(EV.VICTORY, {});
}
function lose(world, reason) {
  if (world.outcome) return;
  world.outcome = 'lose'; world.won = false; world.gameOver = true; world.failReason = reason;
  world.events.emit(EV.DEFEAT, {});
}

export function updateObjectives(world) {
  if (world.outcome) return;
  if (world.player.state === 'dead') { lose(world, 'Commander Benjen Daguth has fallen.'); return; }

  let allDone = true;
  for (const t of world.objectives) {
    if (!t.done) { evalTracker(world, t); if (!t.done) allDone = false; }
  }
  for (const t of world._failConds) {
    if (t.failed) continue;
    evalTracker(world, t);
    if (t.failed) {
      const msg = t.type === 'protect' ? `${t.p.name} has fallen.`
        : t.type === 'timeout' ? 'The Magmar held the field too long.'
        : t.type === 'holdBastions' ? 'The Magmar have overrun your holds.'
        : 'The battle is lost.';
      lose(world, msg); return;
    }
  }
  if (allDone && world.objectives.length) win(world);
}
